'use client';

import { useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { SectionWrapper } from '@/components/ui/section-wrapper';

interface MLMPlan {
    slug: string;
    name: string;
    description: string;
    content: string;
    image?: string;
    features?: string[];
}

interface PlanFallbackContentProps {
    plan: MLMPlan;
}

export function PlanFallbackContent({ plan }: PlanFallbackContentProps) {
    // Sanitize HTML content to prevent XSS attacks
    const sanitizedContent = useMemo(() => {
        if (!plan.content) return null;
        return DOMPurify.sanitize(plan.content, {
            ALLOWED_TAGS: [
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'p', 'br', 'hr',
                'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'small', 'sub', 'sup',
                'ul', 'ol', 'li',
                'blockquote', 'pre', 'code',
                'a', 'img', 'figure', 'figcaption',
                'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
                'div', 'span', 'section', 'article',
            ],
            ALLOWED_ATTR: [
                'href', 'target', 'rel', 'title',
                'src', 'alt', 'width', 'height', 'loading', 'referrerpolicy',
                'class', 'id',
                'colspan', 'rowspan',
            ],
            ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
            FORBID_ATTR: ['onerror', 'onload', 'oninput', 'onclick', 'onmouseover'],
            FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'form', 'input', 'button'],
            ALLOW_DATA_ATTR: false,
        });
    }, [plan.content]);

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950">
            <SectionWrapper variant="default" padding="lg" className="relative">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        {plan.name}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                        {plan.description}
                    </p>
                    {sanitizedContent && (
                        <div
                            className="prose prose-lg dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                        />
                    )}
                </div>
            </SectionWrapper>
        </div>
    );
}

